import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { 
  TIER_CONFIG, 
  TIER_ORDER, 
  type TierType, 
  type Badge, 
  type UserEngagement,
  type PendingReward 
} from '@/types/engagement';
import { ensureSession, fetchUserLevelRow, fetchPublishedCount, combinePoints, tierForPoints } from '@/lib/engagementPoints';

export function useEngagement() {
  const { user, isHydrated } = useAuth();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [userBadges, setUserBadges] = useState<{ badge_id: string }[]>([]);
  const [engagement, setEngagement] = useState<UserEngagement | null>(null);
  const [publishedCount, setPublishedCount] = useState(0);
  const [pendingRewards, setPendingRewards] = useState<PendingReward[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch engagement data
  useEffect(() => {
    if (!isHydrated) return;
    if (!user) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    const fetchEngagementData = async () => {
      setIsLoading(true);
      await ensureSession();

      const [badgesRes, userBadgesRes, levelData, published, rewardsRes] = await Promise.all([
        supabase.from('badges').select('*').order('points', { ascending: true }),
        supabase.from('user_badges').select('badge_id').eq('user_id', user.id),
        fetchUserLevelRow(user.id),
        fetchPublishedCount(user.id),
        supabase
          .from('pending_rewards')
          .select('*')
          .eq('user_id', user.id)
          .is('claimed_at', null)
          .order('created_at', { ascending: false }),
      ]);
      if (cancelled) return;

      if (badgesRes.data) setBadges(badgesRes.data as Badge[]);
      if (userBadgesRes.data) setUserBadges(userBadgesRes.data);
      if (levelData) setEngagement(levelData as unknown as UserEngagement);
      setPublishedCount(published);
      if (rewardsRes.data) setPendingRewards(rewardsRes.data as unknown as PendingReward[]);

      setIsLoading(false);
    };

    fetchEngagementData();
    return () => { cancelled = true; };
  }, [isHydrated, user?.id]);

  // Claim a pending reward
  const claimReward = useCallback(async (rewardId: string) => {
    if (!user) return false;

    const reward = pendingRewards.find(r => r.id === rewardId);
    if (!reward) return false;

    try {
      const { data, error } = await supabase.rpc('claim_pending_reward', { p_reward_id: rewardId });
      if (error) throw error;
      const result = (data as { success?: boolean } | null) || {};
      if (!result.success) return false;

      // Remove from pending
      setPendingRewards(prev => prev.filter(r => r.id !== rewardId));
      return true;
    } catch (error) {
      console.error('Error claiming reward:', error);
      return false;
    }
  }, [user, pendingRewards]);

  // Derived state
  const earnedBadgeIds = userBadges.map(ub => ub.badge_id);
  const earnedBadges = badges.filter(b => earnedBadgeIds.includes(b.id));
  const unearnedBadges = badges.filter(b => !earnedBadgeIds.includes(b.id));

  const totalPoints = combinePoints(engagement?.total_points || 0, publishedCount);
  const currentTier = tierForPoints(totalPoints);
  
  const currentTierIndex = TIER_ORDER.indexOf(currentTier);
  const currentTierConfig = TIER_CONFIG[currentTier];
  
  const nextTier = currentTierIndex < TIER_ORDER.length - 1 
    ? TIER_ORDER[currentTierIndex + 1] as TierType
    : null;
  const nextTierConfig = nextTier ? TIER_CONFIG[nextTier] : null;
  
  const pointsToNextTier = nextTierConfig 
    ? nextTierConfig.minPoints - totalPoints
    : 0;

  const progressInCurrentTier = currentTier === 'beyond'
    ? (totalPoints - 300) % 100
    : Math.min(100, ((totalPoints - currentTierConfig.minPoints) / (currentTierConfig.maxPoints - currentTierConfig.minPoints)) * 100);

  return {
    badges,
    earnedBadges,
    unearnedBadges,
    engagement,
    currentTier,
    currentTierConfig,
    nextTier,
    nextTierConfig,
    totalPoints,
    pointsToNextTier,
    progressInCurrentTier,
    pendingRewards,
    claimReward,
    isLoading,
  };
}
